/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unused-prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Button, Flex, Label, Pane, Text, TextInput } from '@leverege/ui-elements';
import I18N from '@leverege/i18n';
import StringUtil from '@leverege/string-util';
import { Auth } from '@leverege/api-redux';
import { connect } from 'react-redux';
import { Validator } from '@leverege/validator';
import { useMediaQuery } from '@react-hook/media-query';
import { GlobalState, UI } from '@leverege/ui-redux';
import useLogin from "./hooks/useLogin.js";
import useForm from "./hooks/useForm.js";
import FieldErrorsComponent from "./views/FieldErrorsComponent.js";
import S from './Login.css';
const {
  t,
  tf,
  tIcon
} = I18N.ns('login.Login');
function Login(props) {
  const {
    classNames: {
      className,
      forgotClassName,
      labelClassName,
      identityProviderClassname,
      loginByCodeClassname,
      requestDeviceCodeClassname,
      passwordClassName,
      submitClassName,
      userClassName,
      warningClassName
    },
    components: {
      ErrorComponent,
      LoadingComponent,
      LogoComponent
    },
    variants: {
      variant,
      forgotVariant,
      labelVariant,
      identityProviderVariant,
      loginByCodeVariant,
      requestDeviceCodeVariant,
      loginLogoVariant,
      passwordVariant,
      usernameVariant,
      warningVariant
    },
    options: {
      getShouldDisplayLogo,
      loginByCodeEnabled,
      requestDeviceCodeEnabled,
      warningLocation,
      deviceCodeLoginIsDefault
    },
    state: {
      error,
      onSubmit: pOnSubmit
    },
    children,
    identityProviders,
    loginByCodeIcon,
    requestDeviceCodeIcon,
    preLogin,
    showIdentityProviders,
    onIdentityProviderClick,
    onLoginByCodeClick,
    onRequestDeviceCodeClick,
    onForgotPasswordClick,
    isLoggedIn,
    isLoading,
    hasRedirectedToDeviceCode
  } = useLogin(props);
  useEffect(() => {
    if (deviceCodeLoginIsDefault && !hasRedirectedToDeviceCode) {
      GlobalState.dispatch(UI.set('login.hasRedirectedToDeviceCode', true));
      onRequestDeviceCodeClick?.();
    }
  }, [deviceCodeLoginIsDefault]);
  const matches = useMediaQuery('only screen and (min-width: 1600px)');
  const userRef = useRef();
  const passwordRef = useRef();
  const {
    errors,
    values,
    onChange,
    onSubmit
  } = useForm({
    fields: {
      username: {
        validator: Validator.required,
        errorMessage: tf('usernameError')
      },
      password: {
        validator: Validator.required,
        errorMessage: tf('passwordError')
      }
    },
    onSubmit: pOnSubmit
  });
  useEffect(() => {
    const userInterval = setInterval(() => {
      if (userRef.current?.value) {
        onChange({
          data: 'username',
          value: userRef.current.value
        });
        clearInterval(userInterval);
      }
    }, 100);
    const passwordInterval = setInterval(() => {
      if (passwordRef.current?.value) {
        onChange({
          data: 'password',
          value: passwordRef.current.value
        });
        clearInterval(passwordInterval);
      }
    }, 100);
    return () => {
      clearInterval(userInterval);
      clearInterval(passwordInterval);
    };
  }, []);
  const {
    additionalLoginButtons,
    additionalLoginButtonColumns
  } = useMemo(() => {
    let loginButtonCount = 0;
    if (showIdentityProviders && identityProviders?.length > 0) {
      loginButtonCount += identityProviders.length;
    }
    if (loginByCodeEnabled) {
      loginButtonCount += 1;
    }
    if (requestDeviceCodeEnabled) {
      loginButtonCount += 1;
    }
    const loginButtons = [];
    if (showIdentityProviders && identityProviders?.length > 0) {
      identityProviders.slice(0, 1).forEach(provider => {
        loginButtons.push(/*#__PURE__*/React.createElement(Button, {
          key: provider.name,
          variant: `${identityProviderVariant}${loginButtonCount === 1 ? 'Small' : ''}`,
          className: classnames(S.altLoginButton, identityProviderClassname),
          icon: provider.icon,
          onClick: onIdentityProviderClick,
          eventData: provider.loginUrl
        }, /*#__PURE__*/React.createElement(Text, {
          variant: `altLoginMethod${loginButtonCount === 1 ? 'Small' : ''}|captionSmall`,
          className: classnames(S.altLoginMethod, loginButtonCount === 1 && S.altLoginMethodSmall)
        }, StringUtil.capitalize(provider.name))));
      });
    }
    if (loginByCodeEnabled) {
      loginButtons.push(/*#__PURE__*/React.createElement(Button, {
        type: "button",
        key: "loginByCode",
        variant: `${loginByCodeVariant}${loginButtonCount === 1 ? 'Small' : ''}`,
        className: classnames(S.altLoginButton, loginByCodeClassname),
        icon: loginByCodeIcon,
        onClick: onLoginByCodeClick,
        eventData: "request_login_code"
      }, /*#__PURE__*/React.createElement(Text, {
        variant: `altLoginMethod${loginButtonCount === 1 ? 'Small' : ''}|captionSmall`,
        className: classnames(S.altLoginMethod, loginButtonCount === 1 && S.altLoginMethodSmall)
      }, t('requestCodeText'))));
    }
    if (requestDeviceCodeEnabled) {
      loginButtons.push(/*#__PURE__*/React.createElement(Button, {
        type: "button",
        key: "requestDeviceCode",
        variant: `${requestDeviceCodeVariant}${loginButtonCount === 1 ? 'Small' : ''}`,
        className: classnames(S.altLoginButton, requestDeviceCodeClassname),
        icon: requestDeviceCodeIcon,
        onClick: onRequestDeviceCodeClick,
        eventData: "request_device_code"
      }, /*#__PURE__*/React.createElement(Text, {
        variant: `altLoginMethod${loginButtonCount === 1 ? 'Small' : ''}|captionSmall`,
        className: classnames(S.altLoginMethod, loginButtonCount === 1 && S.altLoginMethodSmall)
      }, t('requestDeviceCodeText'))));
    }
    if (showIdentityProviders && identityProviders?.length > 0) {
      identityProviders.slice(1).forEach(provider => {
        loginButtons.push(/*#__PURE__*/React.createElement(Button, {
          key: provider.name,
          variant: `${identityProviderVariant}${loginButtonCount === 1 ? 'Small' : ''}`,
          className: classnames(S.altLoginButton, identityProviderClassname),
          icon: provider.icon,
          onClick: onIdentityProviderClick,
          eventData: provider.loginUrl
        }, /*#__PURE__*/React.createElement(Text, {
          variant: `altLoginMethod${loginButtonCount === 1 ? 'Small' : ''}|captionSmall`,
          className: classnames(S.altLoginMethod, loginButtonCount === 1 && S.altLoginMethodSmall)
        }, StringUtil.capitalize(provider.name))));
      });
    }
    return {
      additionalLoginButtons: loginButtons,
      additionalLoginButtonColumns: Math.min(loginButtons.length, 3)
    };
  }, [showIdentityProviders, identityProviders, loginByCodeEnabled, requestDeviceCodeEnabled, identityProviderClassname, identityProviderVariant, loginByCodeClassname, loginByCodeVariant, loginByCodeIcon, onIdentityProviderClick, onLoginByCodeClick, onRequestDeviceCodeClick, requestDeviceCodeClassname, requestDeviceCodeVariant, requestDeviceCodeIcon]);
  const [showMore, setShowMore] = useState(matches);
  const onToggleShowMore = useCallback(() => {
    setShowMore(!showMore);
  }, [showMore]);
  const displayLogo = getShouldDisplayLogo(props, 'login/login');
  return /*#__PURE__*/React.createElement(Pane, {
    variant: variant,
    className: classnames(className, S.pane),
    layout: "flex:colS"
  }, (isLoggedIn || isLoading) && /*#__PURE__*/React.createElement("div", {
    className: S.blocker
  }, /*#__PURE__*/React.createElement(LoadingComponent, {
    color: "var(--color-brand500)",
    style: {
      position: 'absolute',
      top: '50%',
      left: '50%'
    }
  })), /*#__PURE__*/React.createElement(Flex, {
    inline: true,
    variant: "colM",
    align: "center"
  }, displayLogo && LogoComponent ? /*#__PURE__*/React.createElement(LogoComponent, {
    variant: loginLogoVariant
  }) : null, warningLocation === 'top' && ErrorComponent ? /*#__PURE__*/React.createElement(ErrorComponent, {
    variant: warningVariant,
    className: warningClassName,
    error: error
  }) : null, /*#__PURE__*/React.createElement(Flex, {
    variant: "loginPane",
    className: S.loginPaneFlex
  }, /*#__PURE__*/React.createElement(Pane, {
    variant: "loginPaneHalf"
  }, /*#__PURE__*/React.createElement("form", {
    className: S.form,
    onSubmit: e => {
      e.preventDefault();
    }
  }, /*#__PURE__*/React.createElement(Flex, {
    variant: "colM",
    align: "stretch"
  }, /*#__PURE__*/React.createElement(Label, {
    variant: labelVariant,
    className: labelClassName
  }, t('userText')), /*#__PURE__*/React.createElement(TextInput, {
    domRef: userRef,
    variant: usernameVariant,
    className: userClassName,
    alwaysFireOnReturn: true,
    value: values.username,
    hint: t('userHint'),
    onChange: onChange,
    eventData: "username",
    autoComplete: "username",
    error: !!errors.username
  }), /*#__PURE__*/React.createElement(FieldErrorsComponent, {
    errors: errors.username
  }), /*#__PURE__*/React.createElement(Label, {
    variant: labelVariant,
    className: labelClassName
  }, t('passwordText')), /*#__PURE__*/React.createElement(TextInput, {
    variant: passwordVariant,
    className: passwordClassName,
    domRef: passwordRef,
    password: true,
    showPasswordIcon: tIcon('icon.showPassword', 'fa fa-fw fa-eye'),
    value: values.password,
    alwaysFireOnReturn: true,
    hint: t('passwordHint'),
    onChange: onChange,
    eventData: "password",
    autoComplete: "current-password",
    error: !!errors.password
  }), /*#__PURE__*/React.createElement(FieldErrorsComponent, {
    errors: errors.password
  }), /*#__PURE__*/React.createElement(Flex, {
    variant: "rowEnd",
    alignSelf: "center",
    style: {
      width: '100%',
      marginBottom: 10
    }
  }, /*#__PURE__*/React.createElement(Button, {
    variant: forgotVariant,
    className: forgotClassName,
    type: "button",
    eventData: "forgot_password",
    onClick: onForgotPasswordClick
  }, t('forgotText'))), preLogin, /*#__PURE__*/React.createElement(Button, {
    className: submitClassName,
    type: "submit",
    onClick: onSubmit
  }, t('submit')), warningLocation !== 'top' && ErrorComponent ? /*#__PURE__*/React.createElement(ErrorComponent, {
    variant: warningVariant,
    className: warningClassName,
    error: error
  }) : null))), additionalLoginButtons.length > 0 && /*#__PURE__*/React.createElement(Pane, {
    variant: "loginPaneHalf",
    layout: "flex:colSCenter"
  }, /*#__PURE__*/React.createElement(LoginMethodSeparator, null), /*#__PURE__*/React.createElement("div", {
    className: classnames(S.altLoginMethods, (showMore || matches) && S.showMore),
    style: {
      '--count-loginMethodColumns': additionalLoginButtonColumns
    }
  }, additionalLoginButtons), additionalLoginButtons.length > 3 && !matches && /*#__PURE__*/React.createElement(Button, {
    variant: "linkNoPad",
    onClick: onToggleShowMore
  }, t('show', {
    what: showMore ? 'Less' : 'More'
  })))), children));
}
function LoginMethodSeparator(props) {
  return /*#__PURE__*/React.createElement("div", {
    className: S.separator
  }, /*#__PURE__*/React.createElement(Text, {
    className: S.continueWith,
    variant: "caption"
  }, t('continueWith')), /*#__PURE__*/React.createElement("hr", null));
}
Login.propTypes = {
  onAfterLogin: PropTypes.func,
  onLoginError: PropTypes.func,
  classNames: PropTypes.exact({
    className: PropTypes.string,
    backClassName: PropTypes.string,
    codeClassName: PropTypes.string,
    forgotClassName: PropTypes.string,
    headerClassName: PropTypes.string,
    loginClassName: PropTypes.string,
    passwordClassName: PropTypes.string,
    requestCodeClassName: PropTypes.string,
    resetClassName: PropTypes.string,
    twoFactorClassName: PropTypes.string,
    userClassName: PropTypes.string,
    usernameVariant: PropTypes.string,
    warningClassName: PropTypes.string
  }),
  components: PropTypes.exact({
    ErrorComponent: PropTypes.elementType,
    LoadingComponent: PropTypes.elementType,
    LogoComponent: PropTypes.elementType
  }),
  variants: PropTypes.exact({
    variant: PropTypes.string,
    forgotVariant: PropTypes.string,
    labelVariant: PropTypes.string,
    identityProviderVariant: PropTypes.string,
    loginByCodeVariant: PropTypes.string,
    requestDeviceCodeVariant: PropTypes.string,
    loginLogoVariant: PropTypes.string,
    passwordVariant: PropTypes.string,
    usernameVariant: PropTypes.string,
    warningVariant: PropTypes.string
  })
};
export default connect(state => {
  return {
    isLoggedIn: Auth.isLoggedIn(state.auth),
    isLoading: Auth.isLoading(state.auth),
    hasRedirectedToDeviceCode: UI.get(state, 'login.hasRedirectedToDeviceCode')
  };
})(Login);